import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, ArrowLeft, ArrowRight, Star, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { RegisterCelebration } from '../components/PuroAnimations';
import { API } from '../config/api';
import { isPromoActive, PLAN_PRICING } from '../lib/promo';
import { useLegalVisible } from '../hooks/useLegalVisible';

const PLANS = [
  { id: 'essentielle',    name: 'Essentielle',    sub: '1 cours / semaine',  price: PLAN_PRICING.essentielle.regular,    vipPrice: PLAN_PRICING.essentielle.launch,    desc: 'Idéal pour débuter' },
  { id: 'evolution',      name: 'Évolution',      sub: '2 cours / semaine',  price: PLAN_PRICING.evolution.regular,      vipPrice: PLAN_PRICING.evolution.launch,      recommended: true, desc: 'Le plus populaire' },
  { id: 'transformation', name: 'Transformation', sub: '3 cours / semaine',  price: PLAN_PRICING.transformation.regular, vipPrice: PLAN_PRICING.transformation.launch, desc: 'Résultats accélérés' },
];

const LOGO = "/assets/logo-puro.png";
const LOGO_FILTER = { filter: 'brightness(0) saturate(100%) invert(45%) sepia(28%) saturate(520%) hue-rotate(357deg) brightness(0.92)' };

const slideVariants = {
  enter: { x: 50, opacity: 0 },
  center: { x: 0, opacity: 1 },
  exit: { x: -50, opacity: 0 }
};

export default function Register() {
  const { register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const forceForm = searchParams.get('form') === '1';
  const [step, setStep] = useState((searchParams.get('plan') || forceForm) ? 2 : 1);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [chosenPlan, setChosenPlan] = useState(searchParams.get('plan') || '');
  const [signupSource, setSignupSource] = useState('');
  const [commune, setCommune] = useState('');
  const [acceptCgv, setAcceptCgv] = useState(false);
  const [certify18, setCertify18] = useState(false);
  const [certifyAble, setCertifyAble] = useState(false);
  const [consentNews, setConsentNews] = useState(false);
  const [consentImage, setConsentImage] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [isPrivilege, setIsPrivilege] = useState(false);
  const { visible: legalVisible } = useLegalVisible();
  const debounceRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated && !showCelebration) navigate('/');
  }, [isAuthenticated, showCelebration]);

  // Live re-check when email changes on step 2 (keeps prices accurate if user edits email)
  useEffect(() => {
    if (step !== 2 || !email) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/auth/check-privilege`, { email });
        setIsPrivilege(!!res.data?.is_privilege);
      } catch (e) { /* silent — server stays source of truth */ }
    }, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [email, step]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!chosenPlan || chosenPlan === 'Non choisi') {
      setError('Veuillez sélectionner une formule pour finaliser votre pré-inscription.');
      return;
    }
    if (!signupSource) {
      setError("Merci d'indiquer comment vous nous avez connue.");
      return;
    }
    if (!commune) {
      setError('Veuillez sélectionner votre commune.');
      return;
    }
    if ((legalVisible && !acceptCgv) || !certify18 || !certifyAble) {
      setError(legalVisible
        ? 'Merci de cocher les trois cases obligatoires (CGV, majorité, aptitude).'
        : 'Merci de cocher les deux cases obligatoires (majorité, aptitude).');
      return;
    }
    setLoading(true);
    setShowCelebration(true);
    const result = await register(name, email, password, phone || null, chosenPlan, signupSource, {
      commune, accept_cgv: legalVisible && acceptCgv, certify_18: certify18, certify_able: certifyAble,
      consent_newsletter: consentNews, consent_image: consentImage,
    });
    if (result.success) {
      // Server is the source of truth on privilege status
      setIsPrivilege(result.data?.is_privilege || false);
    } else {
      setShowCelebration(false);
      setError(result.error);
    }
    setLoading(false);
  };

  if (showCelebration) {
    return <RegisterCelebration name={name} isPrivilege={isPrivilege} onComplete={() => navigate('/')} />;
  }

  // During the launch window, EVERYONE sees the −20% pricing. After PROMO_END_TS, only confirmed VIP members do.
  const showDiscount = isPromoActive() || isPrivilege;
  const priceFor = (plan) => (showDiscount ? plan.vipPrice : plan.price);

  return (
    <div className="min-h-screen bg-[#F5EFE5] flex flex-col">
      {/* Minimal header */}
      <div className="p-4 sm:p-6">
        <button onClick={() => step > 1 ? setStep(step - 1) : navigate('/')} className="inline-flex items-center text-[#7A6455] hover:text-[#C99A55] transition-colors text-sm">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
          {step > 1 ? 'Retour' : 'Accueil'}
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-12">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait">
            {/* STEP 1: Choose experience */}
            {step === 1 && (
              <motion.div key="step1" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.4 }}>
                <div className="text-center mb-8">
                  <img src={LOGO} alt="PURO" className="h-14 w-auto mx-auto mb-5" style={LOGO_FILTER} />
                  <h1 className="font-heading font-medium text-2xl sm:text-3xl text-[#493329] tracking-tight">Choisissez votre expérience</h1>
                  <div className="w-8 h-0.5 bg-[#C99A55] mx-auto mt-3" />
                </div>

                {/* Pre-launch (-20%) checker — REMOVED on user request: promo is active for everyone, the block was friction. */}

                <div className="space-y-3">
                  {PLANS.map((plan) => (
                    <button
                      key={plan.id}
                      onClick={() => { setChosenPlan(plan.id); setStep(2); }}
                      className={`w-full text-left p-5 rounded-2xl border-2 transition-all duration-300 hover:border-[#C99A55] ${
                        plan.recommended ? 'border-[#C99A55] bg-[#C99A55]/5' : 'border-[#E9DDCC] bg-white'
                      }`}
                      data-testid={`plan-select-${plan.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="font-heading font-medium text-lg text-[#493329]">{plan.name}</span>
                            {plan.recommended && (
                              <span className="bg-[#C99A55] text-white text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Star className="w-2 h-2" fill="currentColor" /> Populaire
                              </span>
                            )}
                            {showDiscount && (
                              <span className="bg-[#493329] text-[#C99A55] text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold">
                                -20%
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-[#7A6455]">{plan.sub} — {plan.desc}</span>
                        </div>
                        <div className="text-right flex items-center gap-2">
                          <div>
                            {showDiscount ? (
                              <div className="flex items-baseline gap-1.5 justify-end">
                                <span className="text-xs text-[#AE9885] line-through">{plan.price}€</span>
                                <span className="text-2xl font-heading font-medium text-[#C99A55]">{plan.vipPrice}</span>
                              </div>
                            ) : (
                              <span className="text-2xl font-heading font-medium text-[#493329]">{plan.price}</span>
                            )}
                            <span className="text-xs text-[#7A6455] block text-right">€/mois</span>
                          </div>
                          <ArrowRight className="w-4 h-4 text-[#C99A55]" strokeWidth={1.5} />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                {/* Cours unité + privé */}
                <div className="grid grid-cols-2 gap-3 mt-5">
                  <button
                    onClick={() => { setChosenPlan('single'); setStep(2); }}
                    className="text-center p-4 rounded-2xl border-2 border-[#E9DDCC] bg-white hover:border-[#C99A55] transition-all duration-300"
                    data-testid="plan-select-single"
                  >
                    <span className="font-heading font-medium text-[#493329] block">Cours à l'unité</span>
                    <span className="text-xl font-heading font-medium text-[#493329]">35<span className="text-xs text-[#7A6455]">€</span></span>
                    <span className="text-[10px] text-[#AE9885] block mt-0.5">Sans engagement</span>
                  </button>
                  <Link to="/cours-prive" className="text-center p-4 rounded-2xl border-2 border-[#E9DDCC] bg-white hover:border-[#C99A55] transition-all duration-300 flex flex-col justify-center" data-testid="plan-select-private">
                    <span className="font-heading font-medium text-[#493329] block">Cours privé</span>
                    <span className="text-sm font-heading font-medium text-[#C99A55]">Tarif sur demande</span>
                  </Link>
                </div>
                <p className="text-center text-xs text-[#AE9885] mt-6">Engagement 12 mois · 6 places par séance</p>
                <div className="mt-6 text-center">
                  <Link to="/login" className="text-sm text-[#7A6455] hover:text-[#C99A55] transition-colors">
                    Déjà membre ? Se connecter
                  </Link>
                </div>
              </motion.div>
            )}

            {/* STEP 2: Form */}
            {step === 2 && (
              <motion.div key="step2" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.4 }}>
                <div className="text-center mb-8">
                  <img src={LOGO} alt="PURO" className="h-14 w-auto mx-auto mb-5" style={LOGO_FILTER} />
                  <h1 className="font-heading font-medium text-2xl sm:text-3xl text-[#493329] tracking-tight">Encore une étape pour accéder à votre séance</h1>
                  <div className="w-8 h-0.5 bg-[#C99A55] mx-auto mt-3 mb-4" />
                  {chosenPlan && (() => {
                    const plan = PLANS.find(p => p.id === chosenPlan);
                    if (!plan) return <p className="text-sm text-[#C99A55] font-medium">Cours à l'unité · 35€</p>;
                    return (
                      <p className="text-sm text-[#C99A55] font-medium">
                        Formule {plan.name} · {showDiscount ? (
                          <>
                            <span className="line-through text-[#AE9885] mr-1">{plan.price}€</span>
                            <span className="font-semibold">{plan.vipPrice}€/mois</span>
                            <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#493329] text-[#C99A55] px-2 py-0.5 rounded-full">-20%</span>
                          </>
                        ) : `${plan.price}€/mois`}
                      </p>
                    );
                  })()}
                  {isPrivilege && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      className="text-xs text-[#7A6455] italic mt-2"
                      data-testid="vip-step2-msg"
                    >
                      Votre accès Prélancement est activé — conditions exclusives.
                    </motion.p>
                  )}
                </div>

                <Card className="border-[#E9DDCC] rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                  <CardContent className="p-6 sm:p-8">
                    <form onSubmit={handleSubmit} className="space-y-4">
                      {error && (
                        <Alert variant="destructive" className="bg-[#C76542]/10 border-[#C76542]/20 text-[#C76542]">
                          <AlertDescription>{error}</AlertDescription>
                        </Alert>
                      )}

                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-[#493329] text-sm">Nom complet</Label>
                        <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)}
                          className="h-12 rounded-xl border-[#E9DDCC] bg-white" placeholder="Marie Dupont" required data-testid="register-name" />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-[#493329] text-sm">Email</Label>
                        <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                          className="h-12 rounded-xl border-[#E9DDCC] bg-white" placeholder="vous@exemple.com" required data-testid="register-email" />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-[#493329] text-sm">Téléphone</Label>
                        <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                          className="h-12 rounded-xl border-[#E9DDCC] bg-white" placeholder="+33 6 12 34 56 78" data-testid="register-phone" />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="password" className="text-[#493329] text-sm">Mot de passe</Label>
                        <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                          className="h-12 rounded-xl border-[#E9DDCC] bg-white" placeholder="6 caractères minimum" minLength={6} required data-testid="register-password" />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="plan" className="text-[#493329] text-sm">Formule qui vous intéresse <span className="text-[#C99A55]">*</span></Label>
                        <select
                          id="plan"
                          value={chosenPlan}
                          onChange={(e) => setChosenPlan(e.target.value)}
                          required
                          data-testid="register-plan"
                          className="flex h-12 w-full rounded-xl border border-[#E9DDCC] bg-white px-3 py-2 text-base text-[#493329] placeholder:text-[#AE9885] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C99A55] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <option value="">— Sélectionnez votre formule —</option>
                          <option value="essentielle">Essentielle — 1 cours/semaine</option>
                          <option value="evolution">Évolution — 2 cours/semaine (populaire)</option>
                          <option value="transformation">Transformation — 3 cours/semaine</option>
                          <option value="single">Cours à l'unité — 35€</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="source" className="text-[#493329] text-sm">Comment nous avez-vous connue ? <span className="text-[#C99A55]">*</span></Label>
                        <select
                          id="source"
                          value={signupSource}
                          onChange={(e) => setSignupSource(e.target.value)}
                          required
                          data-testid="register-source"
                          className="flex h-12 w-full rounded-xl border border-[#E9DDCC] bg-white px-3 py-2 text-base text-[#493329] placeholder:text-[#AE9885] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C99A55] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <option value="">— Sélectionnez —</option>
                          <option value="Instagram">Instagram</option>
                          <option value="Facebook">Facebook</option>
                          <option value="Google">Google</option>
                          <option value="Recommandation">Recommandation</option>
                          <option value="Autre">Autre</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="commune" className="text-[#493329] text-sm">Votre commune <span className="text-[#C99A55]">*</span></Label>
                        <select
                          id="commune"
                          value={commune}
                          onChange={(e) => setCommune(e.target.value)}
                          required
                          data-testid="register-commune"
                          className="flex h-12 w-full rounded-xl border border-[#E9DDCC] bg-white px-3 py-2 text-base text-[#493329] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C99A55]"
                        >
                          <option value="">— Sélectionnez votre commune —</option>
                          {['Magalas','Fouzilhon','Puissalicon','Saint-Geniès-de-Fontedit','Autignac','Pouzolles','Laurens','Puimisson','Pailhès','Murviel-lès-Béziers','Thézan-lès-Béziers','Lignan-sur-Orb','Roujan','Gabian','Abeilhan','Béziers','Autre'].map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-3 pt-2 border-t border-[#E9DDCC]">
                        {legalVisible && (
                        <label className="flex items-start gap-2.5 cursor-pointer">
                          <input type="checkbox" checked={acceptCgv} onChange={e => setAcceptCgv(e.target.checked)} data-testid="register-cgv"
                            className="mt-0.5 h-4 w-4 rounded border-[#C99A55] accent-[#C99A55]" />
                          <span className="text-xs text-[#7A6455] leading-5">
                            J'accepte les <a href="/legal/cgv" target="_blank" rel="noopener noreferrer" className="text-[#C99A55] underline">Conditions Générales de Vente</a> et j'ai pris connaissance de la <a href="/legal/confidentialite" target="_blank" rel="noopener noreferrer" className="text-[#C99A55] underline">politique de confidentialité</a> <span className="text-[#C99A55]">*</span>
                          </span>
                        </label>
                        )}
                        <label className="flex items-start gap-2.5 cursor-pointer">
                          <input type="checkbox" checked={certify18} onChange={e => setCertify18(e.target.checked)} data-testid="register-age18"
                            className="mt-0.5 h-4 w-4 rounded border-[#C99A55] accent-[#C99A55]" />
                          <span className="text-xs text-[#7A6455] leading-5">Je certifie avoir 18 ans ou plus <span className="text-[#C99A55]">*</span></span>
                        </label>
                        <label className="flex items-start gap-2.5 cursor-pointer">
                          <input type="checkbox" checked={certifyAble} onChange={e => setCertifyAble(e.target.checked)} data-testid="register-aptitude"
                            className="mt-0.5 h-4 w-4 rounded border-[#C99A55] accent-[#C99A55]" />
                          <span className="text-xs text-[#7A6455] leading-5">Je certifie être apte à la pratique du Pilates ou avoir consulté un médecin <span className="text-[#C99A55]">*</span></span>
                        </label>
                        <label className="flex items-start gap-2.5 cursor-pointer">
                          <input type="checkbox" checked={consentNews} onChange={e => setConsentNews(e.target.checked)} data-testid="register-newsletter"
                            className="mt-0.5 h-4 w-4 rounded border-[#C99A55] accent-[#C99A55]" />
                          <span className="text-xs text-[#7A6455] leading-5">J'accepte de recevoir les actualités et offres de PURO (facultatif)</span>
                        </label>
                        <label className="flex items-start gap-2.5 cursor-pointer">
                          <input type="checkbox" checked={consentImage} onChange={e => setConsentImage(e.target.checked)} data-testid="register-image"
                            className="mt-0.5 h-4 w-4 rounded border-[#C99A55] accent-[#C99A55]" />
                          <span className="text-xs text-[#7A6455] leading-5">J'autorise PURO à utiliser des photos ou vidéos prises au studio (facultatif, modifiable à tout moment)</span>
                        </label>
                      </div>

                      <Button type="submit" className="w-full bg-[#C99A55] hover:bg-[#493329] text-white h-12 rounded-xl btn-premium transition-all duration-400 text-base py-3.5"
                        disabled={loading} data-testid="register-submit">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Valider ma pré-inscription'}
                      </Button>
                    </form>

                    <p className="text-center text-xs text-[#AE9885] mt-4">Aucun paiement requis · Ouverture septembre 2026</p>
                  </CardContent>
                </Card>

                <div className="mt-6 text-center">
                  <Link to="/login" className="text-sm text-[#7A6455] hover:text-[#C99A55] transition-colors">
                    Déjà membre ? Se connecter
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
